import { getWazzupChannelTypeFamily } from '@pages/Admin/CRMSettings/tabs/GeneralPage/components/WazzupCrmSettings/lib/wazzupCrmIntegration.helpers'
import { WAZZUP_OUTGOING_ATTACHMENT_MAX_BYTES } from './crmIntegrationOutgoingAttachment'

/** Семейство канала для правил вложений CRM→Wazzup. */
export type WazzupOutgoingChannelFamily =
  | 'whatsapp'
  | 'waba'
  | 'viber'
  | 'telegram'
  | 'instagram'
  | 'vk'
  | 'max'
  | 'avito'
  | 'cian'
  | 'unknown'

export type WazzupOutgoingAttachmentKind =
  | 'image'
  | 'document'
  | 'video'
  | 'audio'
  | 'voice'

export type WazzupOutgoingChannelRules = {
  family: WazzupOutgoingChannelFamily
  label: string
  /** Расширения без точки, lower-case. */
  images: readonly string[]
  documents: readonly string[]
  videos: readonly string[]
  audios: readonly string[]
  /** Голосовые из рекордера / файлом. */
  voices: readonly string[]
  imageMaxBytes: number
  documentMaxBytes: number
  videoMaxBytes: number
  audioMaxBytes: number
  voiceMaxBytes: number
  allowVoiceRecorder: boolean
}

const DOC_OFFICE = [
  'pdf',
  'zip',
  'gif',
  'doc',
  'docx',
  'xls',
  'xlsx',
  'ppt',
  'pptx',
] as const

const IMG_JPEG_PNG = ['jpeg', 'jpg', 'png'] as const

/** CRM / API send: потолок 10 МБ на любое вложение. */
const crmCap = (channelLimitBytes: number): number =>
  Math.min(channelLimitBytes, WAZZUP_OUTGOING_ATTACHMENT_MAX_BYTES)

const MB = 1024 * 1024

export const resolveWazzupOutgoingChannelFamily = (
  channelType: string | null | undefined,
): WazzupOutgoingChannelFamily => {
  const family = getWazzupChannelTypeFamily(String(channelType ?? ''))
  const normalized = String(channelType ?? '')
    .trim()
    .toLowerCase()

  if (family === 'whatsapp') {
    // wapi в доке Wazzup ближе к WABA (жёстче лимиты картинок).
    if (normalized === 'wapi' || normalized === 'waba') {
      return 'waba'
    }
    return 'whatsapp'
  }
  if (family === 'telegram') return 'telegram'
  if (family === 'max') return 'max'
  if (family === 'vk') return 'vk'
  if (normalized === 'waba') return 'waba'
  if (normalized === 'viber') return 'viber'
  if (normalized === 'instagram' || normalized === 'ig') return 'instagram'
  if (normalized === 'avito') return 'avito'
  if (normalized === 'cian' || normalized === 'cian.ru') return 'cian'
  if (normalized === 'unknown' || !normalized) return 'unknown'
  return 'unknown'
}

const RULES_BY_FAMILY: Record<WazzupOutgoingChannelFamily, WazzupOutgoingChannelRules> = {
  whatsapp: {
    family: 'whatsapp',
    label: 'WhatsApp',
    images: IMG_JPEG_PNG,
    documents: DOC_OFFICE,
    videos: ['mp4'],
    audios: ['aac', 'mp3', 'amr', 'mpeg'],
    voices: ['ogg'],
    imageMaxBytes: crmCap(45 * MB),
    documentMaxBytes: crmCap(45 * MB),
    videoMaxBytes: crmCap(45 * MB),
    audioMaxBytes: crmCap(45 * MB),
    voiceMaxBytes: crmCap(45 * MB),
    allowVoiceRecorder: true,
  },
  waba: {
    family: 'waba',
    label: 'WABA',
    images: IMG_JPEG_PNG,
    documents: ['pdf', 'zip', 'docx', 'doc', 'xls', 'xlsx', 'ppt', 'pptx'],
    videos: ['mp4'],
    audios: ['aac', 'mp3', 'amr', 'm4a', 'ogg'],
    voices: ['ogg'],
    imageMaxBytes: crmCap(5 * MB),
    documentMaxBytes: crmCap(50 * MB),
    videoMaxBytes: crmCap(16 * MB),
    audioMaxBytes: crmCap(16 * MB),
    voiceMaxBytes: crmCap(16 * MB),
    allowVoiceRecorder: true,
  },
  viber: {
    family: 'viber',
    label: 'Viber',
    images: IMG_JPEG_PNG,
    documents: [...DOC_OFFICE, 'txt'],
    videos: ['mp4'],
    audios: ['aac', 'mp3', 'amr', 'mpeg'],
    voices: ['m4a'],
    imageMaxBytes: crmCap(50 * MB),
    documentMaxBytes: crmCap(50 * MB),
    videoMaxBytes: crmCap(50 * MB),
    audioMaxBytes: crmCap(50 * MB),
    voiceMaxBytes: crmCap(50 * MB),
    allowVoiceRecorder: true,
  },
  telegram: {
    family: 'telegram',
    label: 'Telegram',
    images: IMG_JPEG_PNG,
    documents: DOC_OFFICE,
    videos: ['mp4'],
    audios: ['mp3', 'm4a'],
    voices: ['mp3', 'ogg', 'aac', 'wav', 'm4a'],
    imageMaxBytes: crmCap(5 * MB),
    documentMaxBytes: crmCap(20 * MB),
    videoMaxBytes: crmCap(20 * MB),
    audioMaxBytes: crmCap(20 * MB),
    voiceMaxBytes: crmCap(50 * MB),
    allowVoiceRecorder: true,
  },
  instagram: {
    family: 'instagram',
    label: 'Instagram',
    images: ['jpeg', 'jpg', 'png', 'bmp'],
    documents: [],
    videos: [],
    audios: [],
    voices: [],
    imageMaxBytes: crmCap(8 * MB),
    documentMaxBytes: 0,
    videoMaxBytes: 0,
    audioMaxBytes: 0,
    voiceMaxBytes: 0,
    allowVoiceRecorder: false,
  },
  vk: {
    family: 'vk',
    label: 'ВКонтакте',
    images: ['jpg', 'jpeg', 'png', 'gif'],
    documents: DOC_OFFICE,
    videos: [],
    audios: ['mp3'],
    voices: [],
    imageMaxBytes: crmCap(50 * MB),
    documentMaxBytes: crmCap(50 * MB),
    videoMaxBytes: 0,
    audioMaxBytes: crmCap(50 * MB),
    voiceMaxBytes: 0,
    allowVoiceRecorder: false,
  },
  max: {
    family: 'max',
    label: 'MAX',
    images: IMG_JPEG_PNG,
    documents: DOC_OFFICE,
    videos: ['mp4'],
    audios: ['mp3'],
    voices: ['mp3', 'ogg', 'webm', 'm4a', 'aac', 'wav'],
    imageMaxBytes: crmCap(50 * MB),
    documentMaxBytes: crmCap(50 * MB),
    videoMaxBytes: crmCap(50 * MB),
    audioMaxBytes: crmCap(50 * MB),
    voiceMaxBytes: crmCap(50 * MB),
    allowVoiceRecorder: true,
  },
  avito: {
    family: 'avito',
    label: 'Авито',
    images: IMG_JPEG_PNG,
    documents: [],
    videos: ['mp4'],
    audios: [],
    voices: [],
    imageMaxBytes: crmCap(50 * MB),
    documentMaxBytes: 0,
    videoMaxBytes: crmCap(50 * MB),
    audioMaxBytes: 0,
    voiceMaxBytes: 0,
    allowVoiceRecorder: false,
  },
  cian: {
    family: 'cian',
    label: 'Циан',
    images: IMG_JPEG_PNG,
    documents: DOC_OFFICE,
    videos: ['mp4'],
    audios: ['mp3', 'm4a', 'ogg'],
    voices: ['mp3', 'ogg', 'webm', 'm4a'],
    imageMaxBytes: crmCap(50 * MB),
    documentMaxBytes: crmCap(50 * MB),
    videoMaxBytes: crmCap(50 * MB),
    audioMaxBytes: crmCap(50 * MB),
    voiceMaxBytes: crmCap(50 * MB),
    allowVoiceRecorder: true,
  },
  /** Нет типа канала — безопасный минимум + CRM 10 МБ. */
  unknown: {
    family: 'unknown',
    label: 'Wazzup',
    images: IMG_JPEG_PNG,
    documents: ['pdf', 'zip', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'],
    videos: ['mp4'],
    audios: ['mp3', 'm4a', 'ogg'],
    voices: ['ogg', 'webm', 'mp3', 'm4a'],
    imageMaxBytes: WAZZUP_OUTGOING_ATTACHMENT_MAX_BYTES,
    documentMaxBytes: WAZZUP_OUTGOING_ATTACHMENT_MAX_BYTES,
    videoMaxBytes: WAZZUP_OUTGOING_ATTACHMENT_MAX_BYTES,
    audioMaxBytes: WAZZUP_OUTGOING_ATTACHMENT_MAX_BYTES,
    voiceMaxBytes: WAZZUP_OUTGOING_ATTACHMENT_MAX_BYTES,
    allowVoiceRecorder: true,
  },
}

export const getWazzupOutgoingChannelRules = (
  channelType: string | null | undefined,
): WazzupOutgoingChannelRules =>
  RULES_BY_FAMILY[resolveWazzupOutgoingChannelFamily(channelType)]

const getFileExtension = (fileName: string): string => {
  const match = fileName.trim().match(/\.([^.]+)$/i)
  return match ? match[1].toLowerCase() : ''
}

const formatMb = (bytes: number): string => {
  if (bytes < MB) {
    return `${Math.max(1, Math.round(bytes / 1024))} КБ`
  }
  const mb = bytes / MB
  return Number.isInteger(mb) ? `${mb} МБ` : `${mb.toFixed(1)} МБ`
}

const formatExtList = (exts: readonly string[]): string =>
  exts.map((ext) => ext.toUpperCase()).join(', ')

export const inferWazzupOutgoingAttachmentKind = (
  file: File,
  rules: WazzupOutgoingChannelRules,
  options?: { forceVoice?: boolean },
): WazzupOutgoingAttachmentKind | null => {
  if (options?.forceVoice) {
    return rules.allowVoiceRecorder || rules.voices.length > 0 ? 'voice' : null
  }

  const ext = getFileExtension(file.name)
  const mime = (file.type || '').toLowerCase()

  if (
    (ext && rules.images.includes(ext)) ||
    (mime.startsWith('image/') &&
      rules.images.some((allowed) => mime.includes(allowed === 'jpg' ? 'jpeg' : allowed)))
  ) {
    // gif в WhatsApp числится как документ, не image.
    if (ext === 'gif' && rules.documents.includes('gif') && !rules.images.includes('gif')) {
      return 'document'
    }
    return 'image'
  }

  if (
    (ext && rules.videos.includes(ext)) ||
    (mime.startsWith('video/') && rules.videos.some((allowed) => mime.includes(allowed)))
  ) {
    return 'video'
  }

  if (
    (ext && rules.voices.includes(ext) && mime.includes('ogg')) ||
    (options?.forceVoice && rules.voices.length > 0)
  ) {
    if (ext && rules.voices.includes(ext)) {
      return 'voice'
    }
  }

  if (
    (ext && rules.audios.includes(ext)) ||
    (mime.startsWith('audio/') &&
      rules.audios.some((allowed) => mime.includes(allowed)))
  ) {
    return 'audio'
  }

  if (ext && rules.documents.includes(ext)) {
    return 'document'
  }

  if (
    mime.startsWith('application/') ||
    mime === 'text/plain' ||
    mime === 'text/csv'
  ) {
    if (ext && rules.documents.includes(ext)) {
      return 'document'
    }
  }

  return null
}

const maxBytesForKind = (
  rules: WazzupOutgoingChannelRules,
  kind: WazzupOutgoingAttachmentKind,
): number => {
  switch (kind) {
    case 'image':
      return rules.imageMaxBytes
    case 'document':
      return rules.documentMaxBytes
    case 'video':
      return rules.videoMaxBytes
    case 'audio':
      return rules.audioMaxBytes
    case 'voice':
      return rules.voiceMaxBytes
    default:
      return WAZZUP_OUTGOING_ATTACHMENT_MAX_BYTES
  }
}

/** null = ок; иначе текст ошибки для тоста. */
export const validateWazzupOutgoingAttachment = (
  file: File,
  channelType: string | null | undefined,
  options?: { forceVoice?: boolean },
): string | null => {
  const rules = getWazzupOutgoingChannelRules(channelType)

  // Запись с микрофона: формат браузера (webm/ogg/…) — бэк/Wazzup конвертируют.
  if (options?.forceVoice) {
    if (!rules.allowVoiceRecorder) {
      return `Для канала ${rules.label} голосовые сообщения не поддерживаются`
    }
    if (rules.voiceMaxBytes <= 0) {
      return `Для канала ${rules.label} голосовые сообщения не поддерживаются`
    }
    if (file.size > rules.voiceMaxBytes) {
      return `Для ${rules.label} размер голосового не должен превышать ${formatMb(
        rules.voiceMaxBytes,
      )}`
    }
    if (file.size > WAZZUP_OUTGOING_ATTACHMENT_MAX_BYTES) {
      return `Размер вложения для Wazzup не должен превышать ${formatMb(
        WAZZUP_OUTGOING_ATTACHMENT_MAX_BYTES,
      )}`
    }
    return null
  }

  const kind = inferWazzupOutgoingAttachmentKind(file, rules)

  if (!kind) {
    const parts: string[] = []
    if (rules.images.length) {
      parts.push(`изображения (${formatExtList(rules.images)})`)
    }
    if (rules.documents.length) {
      parts.push(`документы (${formatExtList(rules.documents)})`)
    }
    if (rules.videos.length) {
      parts.push(`видео (${formatExtList(rules.videos)})`)
    }
    if (rules.audios.length) {
      parts.push(`аудио (${formatExtList(rules.audios)})`)
    }
    if (parts.length === 0) {
      return `Для канала ${rules.label} вложения не поддерживаются`
    }
    return `Для канала ${rules.label} доступны: ${parts.join('; ')}`
  }

  const maxBytes = maxBytesForKind(rules, kind)
  if (maxBytes <= 0) {
    return `Для канала ${rules.label} этот тип вложения не поддерживается`
  }

  if (file.size > maxBytes) {
    return `Для ${rules.label} размер ${
      kind === 'image'
        ? 'изображения'
        : kind === 'video'
          ? 'видео'
          : kind === 'audio'
            ? 'аудио'
            : 'файла'
    } не должен превышать ${formatMb(maxBytes)}`
  }

  if (file.size > WAZZUP_OUTGOING_ATTACHMENT_MAX_BYTES) {
    return `Размер вложения для Wazzup не должен превышать ${formatMb(
      WAZZUP_OUTGOING_ATTACHMENT_MAX_BYTES,
    )}`
  }

  return null
}

export const getWazzupOutgoingFileAccept = (
  channelType: string | null | undefined,
): string => {
  const rules = getWazzupOutgoingChannelRules(channelType)
  const exts = [
    ...rules.images,
    ...rules.documents,
    ...rules.videos,
    ...rules.audios,
  ]
  const unique = Array.from(new Set(exts))
  return unique.map((ext) => `.${ext}`).join(',')
}

export const getWazzupOutgoingAttachmentsHint = (
  channelType: string | null | undefined,
): string => {
  const rules = getWazzupOutgoingChannelRules(channelType)
  const parts: string[] = []
  if (rules.images.length) {
    parts.push(`фото ${formatExtList(rules.images)} ≤ ${formatMb(rules.imageMaxBytes)}`)
  }
  if (rules.documents.length) {
    parts.push(
      `файлы ${formatExtList(rules.documents)} ≤ ${formatMb(rules.documentMaxBytes)}`,
    )
  }
  if (rules.videos.length) {
    parts.push(`видео ${formatExtList(rules.videos)} ≤ ${formatMb(rules.videoMaxBytes)}`)
  }
  if (parts.length === 0) {
    return `${rules.label}: вложения не поддерживаются, только текст.`
  }
  return `${rules.label}: ${parts.join('; ')}. Лимит CRM API — ${formatMb(
    WAZZUP_OUTGOING_ATTACHMENT_MAX_BYTES,
  )}. Текст и файл уходят отдельными сообщениями.`
}

export const isWazzupOutgoingVoiceRecorderAllowed = (
  channelType: string | null | undefined,
): boolean => getWazzupOutgoingChannelRules(channelType).allowVoiceRecorder

export const getWazzupOutgoingVoiceMaxBytes = (
  channelType: string | null | undefined,
): number => getWazzupOutgoingChannelRules(channelType).voiceMaxBytes
